
import React, { useState, useEffect } from 'react';
import { useWeekStore, getMondayISO, toLocalISODate, parseLocalDate } from '@/store/weekStore';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useProjects } from '@/hooks/useProjects';
import { WeeklyHeader } from './WeeklyHeader';
import { WeeklySummary } from './WeeklySummary';
import { DayCard } from './DayCard';
import { WeeklyActions } from './WeeklyActions';
import { Badge } from '@/components/ui/badge';
import { getCurrentUser as getCurrentUserFromAuth, type CurrentUser } from '@/utils/authUtils';

// Helper pour détecter le mode PIN
const shouldUsePinMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  const authMethod = window.localStorage.getItem('auth_method');
  const managerPin = window.localStorage.getItem('manager_pin');
  return authMethod === 'pin' && Boolean(managerPin);
};

// Helper pour obtenir le PIN utilisateur
const getUserPinOrThrow = (): string => {
  const pin = typeof window !== 'undefined' ? window.localStorage.getItem('manager_pin') : null;
  if (!pin) throw new Error('PIN utilisateur manquant');
  return pin;
};

interface TimeEntry {
  id: string;
  user_id: string;
  project_id: string;
  start_time: string;
  end_time: string | null;
  duration_minutes: number | null;
  description: string | null;
  is_billable: boolean;
}

interface DayEntry {
  date: string;
  dayName: string;
  entries: TimeEntry[];
  totalMinutes: number;
}

export const WeeklyView: React.FC = () => {
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ start: string; end: string; description: string; date: string; projectId: string }>({
    start: '',
    end: '',
    description: '',
    date: '',
    projectId: '',
  });
  const [weekData, setWeekData] = useState<DayEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const { currentWeekStart, setCurrentWeekStart, resetToCurrentWeek } = useWeekStore();
  const { toast } = useToast();
  const { projects } = useProjects();
  const isActive = false;
  const source = 'production';

  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  useEffect(() => {
    const getCurrentUser = async () => {
      const user = await getCurrentUserFromAuth();
      setCurrentUser(user);
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchWeekData();
    }
  }, [currentUser, currentWeekStart, isActive, source]);

  const getCurrentWeekDates = () => {
    const start = parseLocalDate(currentWeekStart);
    const dates: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(toLocalISODate(d));
    }
    return dates;
  };

  const getLocalDateKey = (isoDateTime: string) => {
    const date = new Date(isoDateTime);
    return toLocalISODate(date);
  };

  // Génère le suffixe timezone local (ex: "-04:00" pour EDT)
  const getLocalTzSuffix = () => {
    const offset = new Date().getTimezoneOffset(); // minutes, positif = ouest
    const sign = offset <= 0 ? '+' : '-';
    const h = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
    const m = String(Math.abs(offset) % 60).padStart(2, '0');
    return `${sign}${h}:${m}`;
  };

  const fetchWeekData = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const weekDates = getCurrentWeekDates();
      const startDate = weekDates[0];
      const endDate = weekDates[6];
      const tz = getLocalTzSuffix();

      let timeEntries: TimeEntry[] = [];

      if (shouldUsePinMode()) {
        // Mode PIN : utiliser la RPC PIN mode
        const pin = getUserPinOrThrow();
        const { data, error } = await supabase.rpc('pin_list_user_time_entries', {
          p_pin: pin,
          p_user_id: currentUser.id,
          p_start: `${startDate}T00:00:00${tz}`,
          p_end: `${endDate}T23:59:59${tz}`,
          p_limit: 1000,
          p_offset: 0
        });

        if (error) throw error;
        timeEntries = (data || []) as TimeEntry[];
      } else {
        // Mode JWT : utiliser l'API Supabase standard
        const tableName = isActive && source === 'staging' ? 'time_entries_staging' : 'time_entries';

        const { data, error } = await supabase
          .from(tableName)
          .select('*')
          .eq('user_id', currentUser.id)
          .gte('start_time', `${startDate}T00:00:00${tz}`)
          .lte('start_time', `${endDate}T23:59:59${tz}`)
          .order('start_time', { ascending: true });

        if (error) throw error;
        timeEntries = data || [];
      }

      const weekData: DayEntry[] = weekDates.map((date, index) => {
        const dayEntries = timeEntries.filter(entry => 
          getLocalDateKey(entry.start_time) === date
        );
        const totalMinutes = dayEntries
          .reduce((sum, entry) => {
            if (entry.duration_minutes !== null && entry.duration_minutes !== undefined)
              return sum + entry.duration_minutes;
            if (entry.end_time) {
              const diff = (new Date(entry.end_time).getTime() - new Date(entry.start_time).getTime()) / 60000;
              return sum + Math.max(0, Math.round(diff));
            }
            return sum;
          }, 0);
        
        return {
          date,
          dayName: dayNames[index],
          entries: dayEntries,
          totalMinutes,
        };
      });

      setWeekData(weekData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données de la semaine",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWeekChange = (direction: 'prev' | 'next') => {
    const start = new Date(currentWeekStart);
    const delta = direction === 'prev' ? -7 : 7;
    start.setDate(start.getDate() + delta);
    setCurrentWeekStart(start.toISOString().split('T')[0]);
  };

  const handleEditStart = (entry: TimeEntry) => {
    setEditingEntry(entry.id);
    const startTime = new Date(entry.start_time).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
    const endTime = entry.end_time ? new Date(entry.end_time).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    }) : '';

    setEditValues({
      start: startTime,
      end: endTime,
      description: entry.description || '',
      date: entry.start_time.split('T')[0],
      projectId: entry.project_id,
    });
  };

  const handleDeleteEntry = async (entryId: string) => {
    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée ?');
    if (!confirmed) return;

    try {
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_delete_time_entry', {
          p_pin: pin,
          p_entry_id: entryId,
        });
        if (error) throw error;
      } else {
        const tableName = isActive && source === 'staging' ? 'time_entries_staging' : 'time_entries';
        const { error } = await supabase
          .from(tableName)
          .delete()
          .eq('id', entryId);
        if (error) throw error;
      }

      toast({
        title: "Succès",
        description: "Entrée supprimée",
      });

      if (editingEntry === entryId) {
        setEditingEntry(null);
      }
      fetchWeekData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'entrée",
        variant: "destructive",
      });
    }
  };

  const handleEditSave = async (entryId: string) => {
    try {
      const entry = weekData.flatMap(day => day.entries).find(e => e.id === entryId);
      if (!entry) return;

      const entryDate = editValues.date || entry.start_time.split('T')[0];
      const startDateTime = new Date(`${entryDate}T${editValues.start}:00`);
      const endDateTime = new Date(`${entryDate}T${editValues.end}:00`);
      
      const durationMinutes = Math.round((endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60));

      if (shouldUsePinMode()) {
        // Mode PIN : utiliser la RPC PIN mode avec p_start_time pour permettre
        // aux employés de modifier la date/heure de début de leurs entrées
        const pin = getUserPinOrThrow();

        const { error } = await supabase.rpc('pin_update_time_entry', {
          p_pin: pin,
          p_entry_id: entryId,
          p_start_time: startDateTime.toISOString(),
          p_end_time: endDateTime.toISOString(),
          p_description: editValues.description,
          p_duration_minutes: durationMinutes,
          p_status: null,
          p_is_billable: null,
          p_project_id: editValues.projectId
        });
        if (error) throw error;
      } else {
        // Mode JWT : utiliser l'API Supabase standard
        const tableName = isActive && source === 'staging' ? 'time_entries_staging' : 'time_entries';
        
        const { error } = await supabase
          .from(tableName)
          .update({
            start_time: startDateTime.toISOString(),
            end_time: endDateTime.toISOString(),
            description: editValues.description,
            duration_minutes: durationMinutes,
            updated_at: new Date().toISOString()
          })
          .eq('id', entryId);

        if (error) throw error;
      }

      toast({
        title: "Succès",
        description: "Entrée de temps mise à jour",
      });

      setEditingEntry(null);
      fetchWeekData();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les modifications",
        variant: "destructive",
      });
    }
  };

  const handleEditCancel = () => {
    setEditingEntry(null);
    setEditValues({ start: '', end: '', description: '', date: '', projectId: '' });
  };

  const handleEditValueChange = (field: 'start' | 'end' | 'description' | 'date' | 'projectId', value: string) => {
    setEditValues(prev => ({ ...prev, [field]: value }));
  };

  const handleAddEntry = async (date: string, data: {
    projectId: string;
    startTime: string;
    endTime: string;
    description: string;
  }) => {
    if (!currentUser) return;

    try {
      const startDateTime = new Date(`${date}T${data.startTime}:00`);
      const endDateTime = new Date(`${date}T${data.endTime}:00`);
      const durationMinutes = Math.round((endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60));

      if (shouldUsePinMode()) {
        // Mode PIN : créer l'entrée avec pin_create_time_entry puis mettre à jour avec end_time
        const pin = getUserPinOrThrow();

        const { data: entryId, error: createError } = await supabase.rpc('pin_create_time_entry', {
          p_pin: pin,
          p_user_id: currentUser.id,
          p_project_id: data.projectId,
          p_start_time: startDateTime.toISOString(),
          p_description: data.description || null,
          p_is_billable: true,
          p_status: 'closed',
        });

        if (createError) throw createError;
        if (!entryId) throw new Error("Aucun ID d'entrée retourné");

        // Mettre à jour avec l'heure de fin
        const { error: updateError } = await supabase.rpc('pin_update_time_entry', {
          p_pin: pin,
          p_entry_id: entryId,
          p_start_time: null,
          p_end_time: endDateTime.toISOString(),
          p_duration_minutes: durationMinutes,
          p_description: null,
          p_is_billable: null,
          p_status: null,
          p_project_id: null,
        });

        if (updateError) throw updateError;
      } else {
        // Mode JWT : utiliser l'API Supabase standard
        const tableName = isActive && source === 'staging' ? 'time_entries_staging' : 'time_entries';

        const { error } = await supabase
          .from(tableName)
          .insert({
            user_id: currentUser.id,
            project_id: data.projectId,
            start_time: startDateTime.toISOString(),
            end_time: endDateTime.toISOString(),
            duration_minutes: durationMinutes,
            description: data.description || null,
            is_billable: true,
            status: 'closed',
          });

        if (error) throw error;
      }

      toast({
        title: "Succès",
        description: "Entrée de temps créée",
      });

      fetchWeekData();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer l'entrée de temps",
        variant: "destructive",
      });
    }
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Projet non trouvé';
  };

  const weekTotal = weekData.reduce((sum, day) => sum + day.totalMinutes, 0);
  const weekStartDate = weekData.length > 0 ? weekData[0].date : currentWeekStart;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4 flex items-center justify-center">
        <div className="text-lg">Chargement des données...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <WeeklyHeader 
            weekStartDate={weekStartDate}
            weekEndDate={weekData.length > 0 ? weekData[6].date : weekStartDate}
            onSelectWeek={(iso) => setCurrentWeekStart(iso)}
          />
          {isActive && source === 'staging' && (
            <Badge variant="secondary" className="text-xs">
              Mode Staging
            </Badge>
          )}
        </div>
        
        <WeeklySummary weekTotal={weekTotal} weekData={weekData} />

        <div className="space-y-4">
          {weekData.map(day => (
            <DayCard
              key={day.date}
              day={day}
              getProjectName={getProjectName}
              projects={projects}
              editingEntry={editingEntry}
              editValues={editValues}
              onEditStart={handleEditStart}
              onEditSave={handleEditSave}
              onEditCancel={handleEditCancel}
              onEditValueChange={handleEditValueChange}
              onDeleteEntry={handleDeleteEntry}
              onAddEntry={handleAddEntry}
            />
          ))}
        </div>

        <WeeklyActions 
          weekData={weekData}
          currentUser={currentUser}
          onWeekChange={handleWeekChange}
        />
      </div>
    </div>
  );
};
